import { useState } from 'react';

/**
 * Returns a per-thread resource whose object identity is **stable for
 * the lifetime of one `threadId`** and is **re-created during render**
 * the moment `threadId` changes.
 *
 * ## Why `useState` + render-time reset, and NOT `useMemo`
 *
 * This is the React-idiomatic "adjust state when a prop changes during
 * render" pattern: `useState` holds `{ threadId, resource }`, and a
 * render-time mismatch builds the new thread's resource immediately —
 * before any child reads it — then `setState`s it for subsequent
 * renders. This repoints the resource to the active thread without an
 * effect round-trip or a stale frame.
 *
 * `useMemo` is deliberately avoided. Under React 19 + the React
 * Compiler, `useMemo` is for derived values, and even a bare
 * `const r = factory()` relying on the compiler to memoize is not
 * load-bearing: the compiler may bail out for surrounding-code reasons,
 * making identity stability unreliable in practice (it reproduces
 * intermittently). `useState` is the tool for an identity-stable
 * resource — it hoists the guarantee out of the compiler's hands.
 *
 * `factory` is invoked exactly once per distinct `threadId`: on initial
 * mount (lazy initializer) and again only when `threadId` changes. It
 * is NOT called on re-renders that keep the same `threadId`.
 *
 * @param threadId - identity key; a change re-creates the resource.
 * @param factory - builds the resource for the current `threadId`.
 *   Closes over its own dependencies (e.g. a query client). Read at
 *   the time of a `threadId` change, so it must reflect the new id.
 */
export function useThreadKeyedResource<ResourceType>(threadId: string, factory: () => ResourceType): ResourceType {
  const [state, setState] = useState(() => ({ threadId, resource: factory() }));

  if (state.threadId !== threadId) {
    const next = { threadId, resource: factory() };
    setState(next);
    return next.resource;
  }

  return state.resource;
}
